/**
 * useProgressiveLoader — Phase 6a.11 per Spec §7.2 Layer 2.
 *
 * Drives the progressive-disclosure message shown in long-running loaders
 * (currently the CalculateLoadingOverlay):
 *
 *   • `normal`     — for the first 5 seconds
 *   • `warming`    — 5 s ≤ elapsed < 15 s
 *   • `coldStart`  — elapsed ≥ 15 s
 *
 * When the loader closes (`active` flips to false) the phase resets so the
 * next open starts fresh. Pure timer-based; the caller decides what
 * copy/UI to render for each phase.
 */

import * as React from "react";

export type ProgressivePhase = "normal" | "warming" | "coldStart";

const WARMING_AFTER_MS = 5_000;
const COLD_START_AFTER_MS = 15_000;

export function useProgressiveLoader(active: boolean): ProgressivePhase {
  const [phase, setPhase] = React.useState<ProgressivePhase>("normal");

  React.useEffect(() => {
    if (!active) {
      setPhase("normal");
      return;
    }
    const warmingTimer = setTimeout(
      () => setPhase("warming"),
      WARMING_AFTER_MS,
    );
    const coldTimer = setTimeout(
      () => setPhase("coldStart"),
      COLD_START_AFTER_MS,
    );
    return () => {
      clearTimeout(warmingTimer);
      clearTimeout(coldTimer);
    };
  }, [active]);

  return phase;
}
